import { useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";

const services = [
  {
    title: "Totalunternehmung (TU)",
    description: "Wir übernehmen Ihr Bauprojekt von A bis Z – Planung, Koordination und Ausführung aus einer Hand. Sie haben einen Ansprechpartner für alles.",
    color: "bg-komel-accent",
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
  },
  {
    title: "Generalunternehmung (GU)",
    description: "Professionelle Bauausführung nach Ihren Plänen. Wir koordinieren alle Gewerke und garantieren termingerechte Fertigstellung.",
    color: "bg-komel-primary",
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80"
  },
  {
    title: "Sanierungen im Bestand",
    description: "Energetische Sanierungen und Modernisierungen bestehender Gebäude – wertvoll für Substanzerhalt und Wertsteigerung.",
    color: "bg-[#E74C3C]",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80"
  },
  {
    title: "Renovationen / Umbauten",
    description: "Umbauten und Renovationen nach Ihren Wünschen. Wir verwandeln Ihre Räume in moderne, funktionale Lebensräume.",
    color: "bg-komel-primary",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80"
  },
  {
    title: "Koordination & Bauleitung",
    description: "Professionelle Baustellenkoordination und Projektsteuerung. Wir halten alle Fäden in der Hand.",
    color: "bg-[#E74C3C]",
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80"
  },
  {
    title: "Arbeiten rund ums Haus",
    description: "Aussenbereich, Dach, Keller, Umgebungsarbeiten – alles was Ihr Zuhause komplett macht.",
    color: "bg-komel-accent",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"
  }
];

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Determine if card is in top row (index 0-2) or bottom row (index 3-5)
  const isTopRow = (index) => index < 3;

  return (
    <section 
      ref={sectionRef}
      id="leistungen" 
      className="py-24 md:py-32 bg-white relative z-10"
      data-testid="services-section"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-komel-primary" data-testid="services-title">
            Unsere Leistungen, Ihre Ergebnisse
          </h2>
        </motion.div>

        {/* Services Grid - HRS Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              data-testid={`service-card-${index}`}
            >
              {/* Main Card */}
              <div 
                className={`${service.color} p-8 h-[320px] md:h-[380px] flex flex-col justify-between transition-all duration-500 cursor-pointer relative overflow-hidden`}
              >
                {/* Image overlay inside card - for top row cards, image slides up from bottom */}
                {/* For bottom row cards, image slides down from top (covering card above) */}
                <AnimatePresence>
                  {hoveredIndex === index && (
                    <motion.div
                      initial={{ 
                        [isTopRow(index) ? 'y' : 'y']: isTopRow(index) ? '100%' : '-100%',
                        opacity: 0 
                      }}
                      animate={{ 
                        y: 0,
                        opacity: 1 
                      }}
                      exit={{ 
                        [isTopRow(index) ? 'y' : 'y']: isTopRow(index) ? '100%' : '-100%',
                        opacity: 0 
                      }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 z-20"
                    >
                      <img 
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      {/* Overlay gradient for readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      {/* Title on image */}
                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="font-heading text-xl md:text-2xl font-semibold text-white">
                          {service.title}
                        </h3>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-white/90 leading-relaxed text-sm md:text-base">
                    {service.description}
                  </p>
                </div>

                {/* Arrow Link */}
                <div className="relative z-10 flex items-center gap-2 text-white group">
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-2" />
                  <span className="font-medium text-sm">Mehr erfahren</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
